import { ClockCircleOutlined, FlagOutlined } from '@ant-design/icons';
import { Drawer, Flex, Tag, Typography } from 'antd';
import type { VendorErrorCodeResponseDto } from '@evimo/client/models';
import { useTranslation } from 'react-i18next';
import { sourceTagColor } from '~/app/components/errorManagement/ErrorManagementPage/utils';
import { TimestampCards } from '~/app/components/errorManagement/TimestampCards/TimestampCards';
import { VendorErrorCodeCatalogSections } from '~/app/components/errorManagement/VendorErrorCodeCatalogSections/VendorErrorCodeCatalogSections';
import {
  ViewDrawerSectionHeader,
  type ViewDrawerSectionTone,
} from '~/app/components/errorManagement/ViewDrawerSectionHeader/ViewDrawerSectionHeader';
import { ViewDrawerTitle } from '~/app/components/errorManagement/ViewDrawerTitle/ViewDrawerTitle';

export interface VendorErrorCodeViewDrawerProps {
  open: boolean;
  record: VendorErrorCodeResponseDto | null;
  onClose: () => void;
}

const TIMESTAMPS_TONE = {
  accent: 'var(--evimo-text-muted)',
  bg: 'color-mix(in srgb, var(--evimo-text-muted) 12%, transparent)',
  icon: <ClockCircleOutlined />,
} as const satisfies ViewDrawerSectionTone;

export function VendorErrorCodeViewDrawer({
  open,
  record,
  onClose,
}: VendorErrorCodeViewDrawerProps) {
  const { t } = useTranslation('errorManagement');

  return (
    <Drawer
      open={open}
      width={560}
      title={
        <ViewDrawerTitle
          title={t('viewDrawer.title')}
          errorCode={record?.errorCode}
        />
      }
      onClose={onClose}
      destroyOnClose
    >
      {record ? (
        <>
          <div
            style={{
              marginBottom: 24,
              padding: '12px 14px',
              borderRadius: 12,
              background:
                'color-mix(in srgb, var(--evimo-blue) 8%, transparent)',
              border:
                '1px solid color-mix(in srgb, var(--evimo-blue) 20%, transparent)',
            }}
          >
            <Flex align="center" justify="space-between" gap={12} wrap="wrap">
              <Flex align="center" gap={8}>
                <FlagOutlined style={{ color: 'var(--evimo-blue)' }} />
                <Typography.Text strong style={{ color: 'var(--evimo-blue)' }}>
                  {t('drawer.source')}
                </Typography.Text>
              </Flex>
              <Tag
                color={sourceTagColor(record.source)}
                style={{ marginInlineEnd: 0 }}
              >
                {record.source}
              </Tag>
            </Flex>
          </div>

          <VendorErrorCodeCatalogSections
            record={record}
            classificationMarginBottom
          />

          <ViewDrawerSectionHeader
            tone={TIMESTAMPS_TONE}
            label={t('viewDrawer.sections.timestamps')}
          />
          <TimestampCards
            items={[
              {
                label: t('viewDrawer.createdAt'),
                value: record.createdAt,
              },
              {
                label: t('viewDrawer.updatedAt'),
                value: record.updatedAt,
              },
            ]}
          />
        </>
      ) : null}
    </Drawer>
  );
}
